/**
 * Creates a deduplicated requestAnimationFrame wrapper.
 *
 * This utility ensures that the provided callback is only scheduled
 * once per animation frame, even if the returned function is called
 * multiple times in quick succession.
 *
 * Useful for high-frequency updates (e.g. DOM mutations, resize events)
 * where you want to avoid redundant work within the same frame.
 *
 * @param callback - The function to execute on the next animation frame.
 * @returns A function that schedules the callback in a deduplicated way.
 *
 * @example
 * const update = requestAnimationFrameThrottled(() => {
 *   console.log('Runs once per frame');
 * });
 *
 * window.addEventListener('resize', update);
 * window.addEventListener('scroll', update);
 */
export const requestAnimationFrameThrottled = (callback: VoidFunction): VoidFunction => {
    // store the current requestAnimationFrame ID if one is scheduled
    let rafId: number | null = null;
    // this is the function that can be called many times, but it will only schedule the callback once per frame.
    return () => {
        // if it has already been scheduled, do nothing
        if (rafId !== null) return;
        // schedule callback via requestAnimationFrame
        rafId = requestAnimationFrame(() => {
            rafId = null;
            callback();
        });
    };
}
